import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { reservationService } from '../../services/reservationService';
import { Loader } from '../../components/Loader';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const Container = styled.div``;

const Title = styled.h1`
  color: ${colors.primary};
  margin-bottom: 30px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 2px solid ${colors.secondary};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 5px;
  }
`;

const Tab = styled.button.attrs(props => ({
  active: undefined,
}))`
  padding: 12px 24px;
  background: none;
  color: ${props => props.active ? colors.primary : colors.textLight};
  font-size: 16px;
  font-weight: 500;
  border-bottom: 3px solid ${props => props.active ? colors.primary : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.3s ease;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const Card = styled.div.attrs(props => ({
  status: undefined,
}))`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px ${colors.shadow};
  border-left: 4px solid ${props => {
    if (props.status === 'approved') return colors.success;
    if (props.status === 'rejected') return colors.error;
    return colors.warning;
  }};

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const ClassTitle = styled.h3`
  color: ${colors.primary};
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ClassInfo = styled.div`
  color: ${colors.text};
  margin-bottom: 8px;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const ClassDescription = styled.p`
  color: ${colors.textLight};
  font-size: 14px;
  margin-bottom: 15px;
`;

const Badge = styled.span.attrs(props => ({
  status: undefined,
}))`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    if (props.status === 'approved') return colors.success;
    if (props.status === 'rejected') return colors.error;
    return colors.warning;
  }};
  color: white;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.textLight};
`;

const CancelButton = styled.button`
  margin-top: 15px;
  padding: 10px 20px;
  background-color: ${colors.error};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    background-color: #c0392b;
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: ${colors.textLight};
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 13px;
  }
`;

const statusLabels = {
  pending: 'Onay Bekliyor',
  approved: 'Onaylandı',
  rejected: 'Reddedildi',
  cancelled: 'İptal Edildi'
};

export const MemberMyReservations = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchReservations();
    }
  }, [user]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const data = await reservationService.getMemberReservations(user.id);
      setReservations(data);
    } catch (error) {
      toast.error('Rezervasyonlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReservation = async (reservationId) => {
    if (!window.confirm('Bu rezervasyonu iptal etmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      setCancellingId(reservationId);
      await reservationService.cancelReservation(reservationId);
      toast.success('Rezervasyon başarıyla iptal edildi');
      fetchReservations(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.error || 'Rezervasyon iptal edilirken hata oluştu');
    } finally {
      setCancellingId(null);
    }
  };

  const filteredReservations = reservations.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });

  // Separate upcoming and past based on class date and time
  const now = new Date();
  const upcomingReservations = filteredReservations.filter(r => {
    if (!r.classDetails?.date) return false;
    
    // If no time, use end of day (23:59:59) for comparison
    const time = r.classDetails.time || '23:59:59';
    const classDateTime = new Date(`${r.classDetails.date}T${time}`);
    
    
    return classDateTime > now;
  });
  const pastReservations = filteredReservations.filter(r => {
    if (!r.classDetails?.date) return true; // If no date, consider as past
    
    // If no time, use end of day (23:59:59) for comparison
    const time = r.classDetails.time || '23:59:59';
    const classDateTime = new Date(`${r.classDetails.date}T${time}`);
    
    
    return classDateTime <= now;
  });

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Title>Rezervasyonlarım</Title>

      <Tabs>
        <Tab active={filter === 'all'} onClick={() => setFilter('all')}>
          Tümü ({reservations.length})
        </Tab>
        <Tab active={filter === 'pending'} onClick={() => setFilter('pending')}>
          Bekleyenler ({reservations.filter(r => r.status === 'pending').length})
        </Tab>
        <Tab active={filter === 'approved'} onClick={() => setFilter('approved')}>
          Onaylananlar ({reservations.filter(r => r.status === 'approved').length})
        </Tab>
        <Tab active={filter === 'rejected'} onClick={() => setFilter('rejected')}>
          Reddedilenler ({reservations.filter(r => r.status === 'rejected').length})
        </Tab>
        <Tab active={filter === 'cancelled'} onClick={() => setFilter('cancelled')}>
          İptal Edilenler ({reservations.filter(r => r.status === 'cancelled').length})
        </Tab>
      </Tabs>

      {upcomingReservations.length > 0 && (
        <>
          <h2 style={{ color: colors.primary, marginBottom: '20px' }}>Yaklaşan Dersler</h2>
          <Grid>
            {upcomingReservations.map(reservation => (
              <Card key={reservation.id} status={reservation.status}>
                <Badge status={reservation.status}>
                  {statusLabels[reservation.status]}
                </Badge>
                
                <ClassTitle>{reservation.classDetails?.title}</ClassTitle>
                <ClassDescription>{reservation.classDetails?.description}</ClassDescription>
                
                <ClassInfo>👨‍🏫 <strong>Eğitmen:</strong> {reservation.classDetails?.instructor}</ClassInfo>
                <ClassInfo>📅 <strong>Tarih:</strong> {reservation.classDetails?.date}</ClassInfo>
                <ClassInfo>🕐 <strong>Saat:</strong> {reservation.classDetails?.time}</ClassInfo>
                
                {reservation.notes && (
                  <ClassInfo>💬 <strong>Notunuz:</strong> {reservation.notes}</ClassInfo>
                )}

                {reservation.status === 'rejected' && reservation.rejectionReason && (
                  <ClassInfo style={{ color: colors.error }}>
                    ❌ <strong>Red Sebebi:</strong> {reservation.rejectionReason}
                  </ClassInfo>
                )}

                {/* Show cancel button for pending and approved reservations that haven't passed */}
                {(() => {
                  const now = new Date();
                  const classDate = reservation.classDetails?.date;
                  const classTime = reservation.classDetails?.time;
                  
                  // Don't show cancel button for cancelled or rejected reservations
                  if (reservation.status === 'cancelled' || reservation.status === 'rejected') {
                    return false;
                  }
                  
                  // For pending reservations, always allow cancellation
                  if (reservation.status === 'pending') {
                    return true;
                  }
                  
                  // For approved reservations, check if class date/time has passed
                  if (reservation.status === 'approved') {
                    if (!classDate || !classTime) {
                      return false; // Can't determine if class has passed
                    }
                    
                    // Create class datetime
                    const classDateTime = new Date(`${classDate}T${classTime}`);
                    
                    // Allow cancellation if class is more than 2 hours away
                    const twoHoursFromNow = new Date(now.getTime() + (2 * 60 * 60 * 1000));
                    return classDateTime > twoHoursFromNow;
                  }
                  
                  return false;
                })() && (
                  <CancelButton
                    onClick={() => handleCancelReservation(reservation.id)}
                    disabled={cancellingId === reservation.id}
                  >
                    {cancellingId === reservation.id ? 'İptal Ediliyor...' : '🗑️ Rezervasyonu İptal Et'}
                  </CancelButton>
                )}
              </Card>
            ))}
          </Grid>
        </>
      )}

      {pastReservations.length > 0 && (
        <>
          <h2 style={{ color: colors.primary, margin: '40px 0 20px', opacity: 0.7 }}>
            Geçmiş Dersler
          </h2>
          <Grid>
            {pastReservations.map(reservation => (
              <Card key={reservation.id} status={reservation.status} style={{ opacity: 0.7 }}>
                <Badge status={reservation.status}>
                  {statusLabels[reservation.status]}
                </Badge>
                
                <ClassTitle>{reservation.classDetails?.title}</ClassTitle>
                <ClassDescription>{reservation.classDetails?.description}</ClassDescription>
                
                <ClassInfo>👨‍🏫 <strong>Eğitmen:</strong> {reservation.classDetails?.instructor}</ClassInfo>
                <ClassInfo>📅 <strong>Tarih:</strong> {reservation.classDetails?.date}</ClassInfo>
                <ClassInfo>🕐 <strong>Saat:</strong> {reservation.classDetails?.time}</ClassInfo>
                
                {/* Show cancel button for pending and approved reservations that haven't passed */}
                {(() => {
                  const now = new Date();
                  const classDate = reservation.classDetails?.date;
                  const classTime = reservation.classDetails?.time;
                  
                  // Don't show cancel button for cancelled or rejected reservations
                  if (reservation.status === 'cancelled' || reservation.status === 'rejected') {
                    return false;
                  }
                  
                  // For pending reservations, always allow cancellation
                  if (reservation.status === 'pending') {
                    return true;
                  }
                  
                  // For approved reservations, check if class date/time has passed
                  if (reservation.status === 'approved') {
                    if (!classDate || !classTime) {
                      return false; // Can't determine if class has passed
                    }
                    
                    // Create class datetime
                    const classDateTime = new Date(`${classDate}T${classTime}`);
                    
                    // Allow cancellation if class is more than 2 hours away
                    const twoHoursFromNow = new Date(now.getTime() + (2 * 60 * 60 * 1000));
                    return classDateTime > twoHoursFromNow;
                  }
                  
                  return false;
                })() && (
                  <CancelButton
                    onClick={() => handleCancelReservation(reservation.id)}
                    disabled={cancellingId === reservation.id}
                  >
                    {cancellingId === reservation.id ? 'İptal Ediliyor...' : '🗑️ Rezervasyonu İptal Et'}
                  </CancelButton>
                )}
              </Card>
            ))}
          </Grid>
        </>
      )}

      {filteredReservations.length === 0 && (
        <EmptyState>
          <p style={{ fontSize: '48px', marginBottom: '20px' }}>📅</p>
          <h3 style={{ color: colors.text, marginBottom: '10px' }}>Rezervasyon Bulunamadı</h3>
          <p>Henüz herhangi bir rezervasyonunuz yok.</p>
        </EmptyState>
      )}
    </Container>
  );
};

